import { Injectable } from "@angular/core";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError, expand, map, last } from "rxjs/operators";
import { SearchParametersQuery, SearchResult, SearchResultItem } from "../../model";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";

/**
 * Service responsible for export functionality and format conversion
 * Handles pagination, data accumulation, and format conversion for exports
 */
@Injectable({
  providedIn: "root",
})
export class ExportFormatterService {
  /**
   * Export search results with smart reuse of already loaded data
   * @param loadedResults Already loaded search results
   * @param searchFn Function to fetch additional results if needed
   * @param params Original search parameters (will be cloned for additional fetching)
   * @param format Export format
   * @param maxResults Maximum number of results to export
   * @param progressCallback Callback to report progress updates
   * @param updateStateCallback Callback to update search state with additional results and pagination info
   * @returns Observable containing exported data
   */
  exportResults(
    loadedResults: SearchResultItem[],
    searchFn: (params: SearchParametersQuery) => Observable<SearchResult>,
    params: SearchParametersQuery,
    format: "csv" | "geojson-polygon" | "geojson-point",
    maxResults: number = 1000,
    progressCallback?: (downloaded: number, total: number) => void,
    updateStateCallback?: (additionalResults: SearchResultItem[], lastPageLoaded: number) => void,
  ): Observable<any> {
    const loadedCount = loadedResults.length;

    if (maxResults <= loadedCount) {
      // Use only already loaded results - no additional API calls needed
      const limitedResults = loadedResults.slice(0, maxResults);
      const exportResult: SearchResult = {
        items: limitedResults,
        total: limitedResults.length,
        metadata: {},
      };

      return new Observable((observer) => {
        try {
          // Report progress immediately for loaded results
          if (progressCallback) {
            progressCallback(maxResults, maxResults);
          }

          const formattedData = this.convertToExportFormat(exportResult, format);
          observer.next(formattedData);
          observer.complete();
        } catch (error) {
          observer.error(error);
        }
      });
    } else {
      // Need to fetch additional results beyond what's loaded
      const additionalNeeded = maxResults - loadedCount;
      return this.fetchAdditionalResults(
        loadedResults,
        searchFn,
        params,
        additionalNeeded,
        format,
        maxResults,
        progressCallback,
        updateStateCallback,
      );
    }
  }

  /**
   * Fetches additional results beyond loaded ones and combines them for export
   * @private
   */
  private fetchAdditionalResults(
    loadedResults: SearchResultItem[],
    searchFn: (params: SearchParametersQuery) => Observable<SearchResult>,
    baseParams: SearchParametersQuery,
    additionalCount: number,
    format: string,
    totalNeeded: number,
    progressCallback?: (downloaded: number, total: number) => void,
    updateStateCallback?: (additionalResults: SearchResultItem[], lastPageLoaded: number) => void,
  ): Observable<any> {
    // Clone parameters to avoid modifying original search state
    const exportParams = { ...baseParams };

    // Keep page size fixed
    const fixedPageSize = baseParams["page-size"] || 100;
    const loadedPages = Math.ceil(loadedResults.length / fixedPageSize);
    const startPage = loadedPages + 1;

    // Create a Set of existing quadkeys for duplicate detection
    const existingQuadkeys = new Set(loadedResults.map((item) => item.quadkey));

    const additionalResults: SearchResultItem[] = [];
    let currentPage = startPage;

    // Report initial progress with loaded results
    if (progressCallback) {
      progressCallback(loadedResults.length, totalNeeded);
    }

    const createRequestParams = (page: number): SearchParametersQuery => ({
      ...exportParams,
      "page-size": fixedPageSize,
      "total-records": totalNeeded,
      page: page,
    });

    return searchFn(createRequestParams(currentPage)).pipe(
      expand((result: SearchResult) => {
        // Filter out duplicates using quadkey
        const newUniqueResults = result.items.filter((item) => !existingQuadkeys.has(item.quadkey));

        // Add new unique quadkeys to our tracking set
        newUniqueResults.forEach((item) => existingQuadkeys.add(item.quadkey));

        // Add to our collection
        additionalResults.push(...newUniqueResults);

        // Report progress with current total
        const currentTotal = loadedResults.length + additionalResults.length;
        if (progressCallback) {
          progressCallback(Math.min(currentTotal, totalNeeded), totalNeeded);
        }

        // Check if we have enough results or should continue
        const hasEnoughResults = additionalResults.length >= additionalCount;
        const receivedFullPage = result.items.length === fixedPageSize;
        const shouldContinueForMore = receivedFullPage && !hasEnoughResults;

        if (shouldContinueForMore) {
          currentPage += 1;
          return searchFn(createRequestParams(currentPage));
        } else {
          return EMPTY;
        }
      }),
      last(),
      map(() => {
        // Combine loaded results with additional fetched results
        const limitedAdditionalResults = additionalResults.slice(0, additionalCount);
        const finalResults = [...loadedResults, ...limitedAdditionalResults];

        // Update search state with additional results for future exports
        const finalPage = Math.ceil((loadedResults.length + limitedAdditionalResults.length) / fixedPageSize);
        if (updateStateCallback && limitedAdditionalResults.length > 0) {
          updateStateCallback(limitedAdditionalResults, finalPage);
        }

        const finalResult: SearchResult = {
          items: finalResults,
          total: finalResults.length,
          metadata: {},
        };

        return this.convertToExportFormat(finalResult, format);
      }),
      catchError((error) => {
        console.error("Export with additional results failed:", error);

        // If we have some results, export those instead of failing completely
        if (additionalResults.length > 0 || loadedResults.length > 0) {
          const limitedResults = additionalResults.slice(0, additionalCount);
          const finalResults = [...loadedResults, ...limitedResults];

          const finalPage = Math.ceil(finalResults.length / fixedPageSize);
          if (updateStateCallback && limitedResults.length > 0) {
            updateStateCallback(limitedResults, finalPage);
          }

          const finalResult: SearchResult = {
            items: finalResults,
            total: finalResults.length,
            metadata: {},
          };

          return new Observable<any>((observer) => {
            observer.next(this.convertToExportFormat(finalResult, format));
            observer.complete();
          });
        } else {
          return throwError(() => error);
        }
      }),
    );
  }

  /**
   * Convert results to export format
   * @private
   */
  private convertToExportFormat(results: SearchResult, format: string): any {
    switch (format) {
      case "csv":
        return this.convertToCSV(results);
      case "geojson-polygon":
        return this.convertToGeoJSON(results);
      case "geojson-point":
        return this.convertToGeoJSONPoint(results);
      default:
        return results;
    }
  }

  /**
   * Convert results to CSV format
   * @private
   */
  private convertToCSV(results: SearchResult): string {
    if (!results.items.length) {
      return "quadkey,similarity,collection,geometry,centroid\n";
    }

    const headers = ["quadkey", "similarity", "collection", "geometry", "centroid"];
    const rows = results.items.map((item) => [
      item.quadkey,
      item.similarity.toString(),
      item.collection || "",
      `"${item.geometry}"`,
      `"${item.centroid}"`,
    ]);

    return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
  }

  /**
   * Convert results to GeoJSON format
   * @private
   */
  private convertToGeoJSON(results: SearchResult): any {
    return {
      type: "FeatureCollection",
      features: results.items.map((item) => ({
        type: "Feature",
        properties: {
          similarity: item.similarity,
          quadkey: item.quadkey,
          collection: item.collection,
          centroid: item.centroid,
        },
        geometry: GeoService.wktToGeoJSONPolygon(item.geometry, false),
      })),
    };
  }

  /**
   * Convert results to GeoJSON Point format
   * @private
   */
  private convertToGeoJSONPoint(results: SearchResult): any {
    return {
      type: "FeatureCollection",
      features: results.items.map((item) => ({
        type: "Feature",
        properties: {
          similarity: item.similarity,
          quadkey: item.quadkey,
          collection: item.collection,
        },
        geometry: GeoService.pointFromArray(item.centroid)?.geometry ?? null,
      })),
    };
  }
}
